"use client";

import {
  PromptInput,
  usePromptInputController,
  PromptInputActionAddAttachments,
  PromptInputActionAddScreenshot,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuTrigger,
  PromptInputAttachmentPreviews,
  PromptInputBody,
  PromptInputButton,
  PromptInputFooter,
  PromptInputSubmit,
  PromptInputTools,
  type PromptInputMessage,
} from "@/components/ai-elements/prompt-input";
import {
  AgentMentionTextarea,
  type PendingAgentMention,
} from "@/components/ai-elements/prompt-input-agent-mention";
import type { MentionableAgent } from "@/lib/agent-mention";
import { Badge } from "@/components/ui/badge";
import { BotMessageSquareIcon } from "@/components/ui/bot-message-square";
import { BookTextIcon } from "@/components/ui/book-text";
import { EarthIcon } from "@/components/ui/earth";
import { TextAnimate } from "@/components/ui/text-animate";
import { TypingAnimation } from "@/registry/magicui/typing-animation";
import { VoiceInputButton } from "@/components/voice-input-button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useGlobalModels } from "@/hooks/use-global-models";
import { type ModelId } from "@/hooks/use-model";
import type { DbAgent } from "@/types/db";
import type { ChatStatus } from "ai";
import type { ComponentProps, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type GreetingPeriod = "morning" | "noon" | "evening" | "night";

const TIME_BASED_GREETINGS: Record<GreetingPeriod, readonly string[]> = {
  morning: [
    "Good morning! Ready to make today count.",
    "Morning! Let's see what the day brings.",
    "A fresh day and a full to-do list. Morning!",
    "Good morning! Let's get started.",
    "Morning! Plenty to do - glad you're here.",
    "Rise and shine. Let's make it a good one.",
    "Good morning! Another day, another opportunity.",
    "Morning! Let's take it one task at a time.",
    "Good morning - let's have a productive one.",
    "Morning! Ready when you are.",
  ],
  noon: [
    "Hi there! Midday already - let's keep going.",
    "Afternoon! Still plenty of day left.",
    "Hello! Checking in from the middle of the day.",
    "Hi! The morning flew by. Let's make the afternoon count.",
    "Midday check-in - what can we work through?",
    "Hello! Half the day done, still plenty ahead.",
    "Hi there! Grabbing a moment between meetings.",
    "Afternoon! Let's make good use of this window.",
    "Hello! Still at it - what's next?",
    "Hi! Midday and hitting my stride.",
  ],
  evening: [
    "Evening! Winding down but not done yet.",
    "Good evening! Let's finish the day well.",
    "Hi! End of day - just a few things left.",
    "Evening! The quiet hours are good for getting things done.",
    "Good evening - one last push for the day.",
    "Hi! Nearly there. Let's wrap this up.",
    "Evening! Grateful for the extra hour of quiet.",
    "Good evening. Let's make the most of what's left.",
    "Hi! Almost done for the day - just this last bit.",
    "Evening! Let's close today out properly.",
  ],
  night: [
    "Still at it - night work has its own rhythm.",
    "Late one tonight. Let's make it worthwhile.",
    "Hi! The world's gone quiet and there's still work to do.",
    "Night check-in - glad something's always on.",
    "It's late but this one couldn't wait.",
    "Burning the midnight oil - let's make it count.",
    "Hi! Late night, clear head. Let's get this done.",
    "The evening's slipped away - just this left to sort.",
    "Night! One last thing and then some well-earned rest.",
    "Late night session - let's finish strong.",
  ],
};

const getGreetingPeriod = (date: Date): GreetingPeriod => {
  const hour = date.getHours();

  if (hour >= 5 && hour < 12) {
    return "morning";
  }

  if (hour >= 12 && hour < 17) {
    return "noon";
  }

  if (hour >= 17 && hour < 22) {
    return "evening";
  }

  return "night";
};

const pickGreeting = (period: GreetingPeriod) => {
  const options = TIME_BASED_GREETINGS[period];
  return options[Math.floor(Math.random() * options.length)] ?? options[0];
};

type PersonalizedGreeting = {
  text: string;
  nameRange: { start: number; end: number } | null;
};

const getDisplayGreeting = (
  userName: string,
  greeting: string | null
): PersonalizedGreeting => {
  const trimmedName = userName.trim();

  if (!greeting) {
    return {
      text: trimmedName,
      nameRange: trimmedName
        ? { start: 0, end: trimmedName.length }
        : null,
    };
  }

  if (!trimmedName) {
    return { text: greeting, nameRange: null };
  }

  const withName = (
    leadingText: string,
    trailingText: string
  ): PersonalizedGreeting => ({
    text: `${leadingText}${trimmedName}${trailingText}`,
    nameRange: {
      start: leadingText.length,
      end: leadingText.length + trimmedName.length,
    },
  });

  const exclamationIndex = greeting.indexOf("!");
  if (exclamationIndex >= 0) {
    return withName(
      `${greeting.slice(0, exclamationIndex)}, `,
      greeting.slice(exclamationIndex)
    );
  }

  const sentenceBreakIndex = greeting.indexOf(". ");
  if (sentenceBreakIndex >= 0) {
    return withName(
      `${greeting.slice(0, sentenceBreakIndex)}, `,
      greeting.slice(sentenceBreakIndex)
    );
  }

  return withName("", `, ${greeting}`);
};

// ── Agent ready phrases ────────────────────────────────────────────────────

const AGENT_READY_PHRASES = [
  "is ready.",
  "is here.",
  "is online.",
  "is standing by.",
  "is at your service.",
  "is listening.",
  "has joined.",
  "is primed and ready.",
  "awaits your prompt.",
  "is ready when you are.",
  "is with you.",
  "is in the room.",
  "is here. Let's get to work.",
  "is ready to help.",
  "is on.",
] as const;

const pickAgentPhrase = () =>
  AGENT_READY_PHRASES[Math.floor(Math.random() * AGENT_READY_PHRASES.length)] ?? AGENT_READY_PHRASES[0];

// ── Code chat ────────────────────────────────────────────────────────────────

// A code chat is the same chat on a live execution sandbox, so it keeps the
// greeting and just wears a console: monospace throughout, and — in place of
// placeholder prose — a block cursor blinking where the caret will land.

// ── Agent summary helpers ────────────────────────────────────────────────────

// (Tool summary intentionally omitted from the welcome heading to reduce clutter.)

// ── Inner component (needs controller context) ───────────────────────────────

type WelcomeScreenInnerProps = {
  effortControl?: ReactNode;
  liveVoiceControl?: ReactNode;
  /** "code" renders the greeting and composer in monospace and parks a
   *  blinking block cursor in the idle composer. */
  variant?: "general" | "code";
  activeAgent?: DbAgent | null;
  activeAgentLabel?: string;
  myFilesLabel?: string | null;
  activeModelLabel?: string;
  activeModel?: ModelId;
  onModelChange?: (model: ModelId) => void;
  incognito?: boolean;
  isUserNameReady?: boolean;
  userName: string;
  onActivateAgent?: () => void;
  mentionableAgents?: MentionableAgent[];
  webSearch: boolean;
  onWebSearchChange: (v: boolean) => void;
  onSend?: (message: PromptInputMessage, agentMention: PendingAgentMention | null) => void;
  status?: ChatStatus;
  onStop?: () => void;
};

const ModelSelector = ({
  value,
  onChange,
  claudeOnly = false,
  codeOnly = false,
}: {
  value?: ModelId;
  onChange?: (model: ModelId) => void;
  /** Widget editing currently requires Anthropic's container. */
  claudeOnly?: boolean;
  codeOnly?: boolean;
}) => {
  const { models: globalModels } = useGlobalModels();
  const activeModels = useMemo(
    () =>
      globalModels.filter(
        (m) => m.is_active && (!claudeOnly || m.provider === "anthropic") &&
          (!codeOnly || m.provider === "anthropic" || m.provider === "openai")
      ),
    [globalModels, claudeOnly, codeOnly]
  );

  if (!value || !onChange || activeModels.length === 0) {
    return null;
  }

  const activeLabel =
    activeModels.find((m) => m.model_id === value)?.name ?? value;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label="Change model"
          className="h-8 gap-1 rounded-full px-3 text-sm font-normal text-muted-foreground hover:text-foreground sm:px-3"
          size="sm"
          type="button"
          variant="ghost"
        >
          <span className="hidden max-w-36 truncate sm:inline">{activeLabel}</span>
          <ChevronDownIcon className="size-3.5 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
          Model
        </DropdownMenuLabel>
        {activeModels.map((m) => (
          <DropdownMenuItem
            className="font-normal"
            key={m.id}
            onSelect={() => {
              if (m.model_id !== value) onChange(m.model_id);
            }}
          >
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate">{m.name}</span>
              {m.tagline ? (
                <span className="truncate text-xs font-normal text-muted-foreground">
                  {m.tagline}
                </span>
              ) : null}
            </span>
            {m.model_id === value ? (
              <CheckIcon className="ml-2 size-4 shrink-0" />
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const WelcomeScreenInner = ({
  effortControl,
  liveVoiceControl,
  variant = "general",
  activeAgent,
  activeAgentLabel,
  myFilesLabel,
  activeModel,
  onModelChange,
  incognito = false,
  isUserNameReady = true,
  userName,
  mentionableAgents = [],
  webSearch,
  onWebSearchChange,
  onSend,
  status,
  onStop,
}: WelcomeScreenInnerProps) => {
  const pendingMentionRef = useRef<PendingAgentMention | null>(null);
  const { textInput } = usePromptInputController();
  const [composerFocused, setComposerFocused] = useState(false);
  const [greeting, setGreeting] = useState<string | null>(null);
  const displayGreeting = getDisplayGreeting(userName, greeting);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- activeAgent.id is intentional: re-pick phrase on agent change
  const agentPhrase = useMemo(() => pickAgentPhrase(), [activeAgent?.id]);

  useEffect(() => {
    const syncGreeting = () => {
      setGreeting((currentGreeting) => {
        const period = getGreetingPeriod(new Date());
        const options = TIME_BASED_GREETINGS[period];

        if (currentGreeting && options.includes(currentGreeting)) {
          return currentGreeting;
        }

        return pickGreeting(period);
      });
    };

    syncGreeting();

    const intervalId = window.setInterval(syncGreeting, 60_000);

    return () => window.clearInterval(intervalId);
  }, []);

  const handleSubmit = useCallback(
    (message: PromptInputMessage) => {
      const mention = pendingMentionRef.current;
      pendingMentionRef.current = null;
      onSend?.(message, mention);
    },
    [onSend]
  );

  // Idle console cursor: only while the composer is empty and unfocused, so it
  // never sits alongside the textarea's own caret.
  const showIdleCursor =
    variant === "code" && !composerFocused && textInput.value.length === 0;

  const textarea = (
    <AgentMentionTextarea
      data-testid="new-chat-input"
      className={variant === "code" ? "font-mono" : undefined}
      placeholder={variant === "code" ? "" : "How can I help you today?"}
      agents={mentionableAgents}
      pendingMentionRef={pendingMentionRef}
      onFocus={() => setComposerFocused(true)}
      onBlur={() => setComposerFocused(false)}
    />
  );

  const composer =
    variant === "code" ? (
      <div className="relative flex w-full flex-1">
        {textarea}
        {showIdleCursor ? (
          // Mirrors the textarea's box and typography so the block sits exactly
          // where the caret appears the moment the user clicks in.
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-start overflow-hidden px-3 py-2.5 font-mono text-base md:text-sm"
          >
            <span className="inline-block h-[1.15em] w-[0.55em] shrink-0 animate-blink-cursor bg-foreground" />
          </div>
        ) : null}
      </div>
    ) : (
      textarea
    );

  const agentHeadingText = activeAgent && agentPhrase ? `${activeAgent.name} ${agentPhrase}` : null;
  const agentNameRange = activeAgent ? { start: 0, end: activeAgent.name.length } : null;

  return (
    <div className="flex w-full max-w-208 flex-col items-center gap-6">
      {/* Heading */}
      <div className="flex max-w-2xl flex-col items-center gap-3 text-center">
        {activeAgent ? (
          <>
            {agentHeadingText ? (
              <TextAnimate
                key={`agent-loaded-${activeAgent.id}-${agentPhrase}`}
                as="h1"
                animation="slideUp"
                by="word"
                className="min-h-16 text-xl font-extralight tracking-tight text-foreground sm:text-4xl"
                once
                segmentClassName={({ start, end }) => {
                  if (!agentNameRange) return "text-inherit";
                  return start < agentNameRange.end && end > agentNameRange.start
                    ? "font-bold text-inherit"
                    : "text-inherit";
                }}
              >
                {agentHeadingText}
              </TextAnimate>
            ) : (
              <div aria-hidden="true" className="min-h-16" />
            )}
            {activeAgent.description ? (
              <TextAnimate
                key={`agent-desc-${activeAgent.id}-${agentPhrase}`}
                as="p"
                animation="blurIn"
                by="word"
                className="text-sm text-muted-foreground"
                delay={0.5}
                once
              >
                {activeAgent.description}
              </TextAnimate>
            ) : null}
          </>
        ) : isUserNameReady ? (
          variant === "code" ? (
            // A console types its output; it does not fly it in word by word.
            // TypingAnimation takes a plain string, so the name loses its bold
            // run here — the typing itself is what carries the emphasis.
            <TypingAnimation
              key={displayGreeting.text || "welcome-greeting"}
              as="h1"
              className="min-h-16 font-mono text-lg font-light leading-tight tracking-tight text-foreground sm:text-3xl"
              cursorStyle="block"
              duration={35}
              startOnView={false}
            >
              {displayGreeting.text || " "}
            </TypingAnimation>
          ) : (
            <TextAnimate
              key={displayGreeting.text || "welcome-greeting"}
              as="h1"
              animation="slideUp"
              by="word"
              className="min-h-16 text-xl font-extralight tracking-tight text-foreground sm:text-4xl"
              once
              segmentClassName={({ start, end }) => {
                const nameRange = displayGreeting.nameRange;

                if (!nameRange) {
                  return "text-inherit";
                }

                return start < nameRange.end && end > nameRange.start
                  ? "font-bold text-inherit"
                  : "text-inherit";
              }}
            >
              {displayGreeting.text || " "}
            </TextAnimate>
          )
        ) : (
          <div aria-hidden="true" className="min-h-16" />
        )}
      </div>

      {/* Prompt input */}
      <div className="w-full">
        <PromptInput
          inputGroupClassName={cn(
            variant === "code" &&
              "border-foreground/40 bg-background has-[[data-slot=input-group-control]:focus-visible]:border-foreground/40 has-[[data-slot=input-group-control]:focus-visible]:ring-0"
          )}
          onSubmit={handleSubmit}
          multiple
        >
          <PromptInputBody>
            <PromptInputAttachmentPreviews />
            {composer}
          </PromptInputBody>
          <PromptInputFooter>
            <PromptInputTools>
              <PromptInputActionMenu>
                <PromptInputActionMenuTrigger
                  tooltip={{ content: "Add attachment", side: "top" }}
                />
                <PromptInputActionMenuContent>
                  <PromptInputActionAddAttachments />
                  <PromptInputActionAddScreenshot />
                </PromptInputActionMenuContent>
              </PromptInputActionMenu>
              <PromptInputButton
                aria-label={webSearch ? "Web search on" : "Web search off"}
                tooltip={{ content: webSearch ? "Web search on" : "Web search off", side: "top" }}
                onClick={() => onWebSearchChange(!webSearch)}
                className="text-muted-foreground"
              >
                <EarthIcon
                  className={webSearch ? "text-activated" : "text-muted-foreground"}
                  size={16}
                />
              </PromptInputButton>
            </PromptInputTools>
            <PromptInputTools>
              <ModelSelector
                value={activeModel}
                onChange={onModelChange}
                codeOnly={variant === "code"}
              />
              {effortControl}
              {activeAgentLabel ? (
                <Badge
                  className={cn(
                    "h-7 max-w-40 min-w-0 gap-1 rounded-full bg-muted px-2 text-[11px]",
                    incognito ? "text-activated" : "text-foreground"
                  )}
                  variant="outline"
                >
                  <BotMessageSquareIcon aria-hidden="true" className="shrink-0" size={10} />
                  <span className="truncate">{activeAgentLabel}</span>
                </Badge>
              ) : null}
              {myFilesLabel ? (
                <Badge
                  className={cn(
                    "h-7 max-w-40 min-w-0 gap-1 rounded-full bg-muted px-2 text-[11px]",
                    incognito ? "text-activated" : "text-foreground"
                  )}
                  variant="outline"
                  title={`This chat is scoped to: ${myFilesLabel}`}
                >
                  <BookTextIcon aria-hidden="true" className="shrink-0" size={10} />
                  <span className="truncate">{myFilesLabel}</span>
                </Badge>
              ) : null}
              <VoiceInputButton />
              {liveVoiceControl}
              <PromptInputSubmit onStop={onStop} status={status} />
            </PromptInputTools>
          </PromptInputFooter>
        </PromptInput>
      </div>
    </div>
  );
};

// ── Public component ─────────────────────────────────────────────────────────

export type WelcomeScreenProps = Omit<ComponentProps<"div">, "children"> &
  WelcomeScreenInnerProps & {
    userName: string;
  };

export const WelcomeScreen = ({
  effortControl,
  liveVoiceControl,
  variant,
  activeAgent,
  activeAgentLabel,
  myFilesLabel,
  activeModelLabel,
  activeModel,
  onModelChange,
  incognito,
  isUserNameReady,
  userName,
  mentionableAgents,
  webSearch,
  onWebSearchChange,
  onActivateAgent,
  onSend,
  status,
  onStop,
  className,
  ...props
}: WelcomeScreenProps) => {
  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 items-center justify-center overflow-hidden px-4 py-12",
        className
      )}
      {...props}
    >
        <WelcomeScreenInner
          effortControl={effortControl}
          liveVoiceControl={liveVoiceControl}
          variant={variant}
          activeAgent={activeAgent}
          activeAgentLabel={activeAgentLabel}
          myFilesLabel={myFilesLabel}
          activeModelLabel={activeModelLabel}
          activeModel={activeModel}
          onModelChange={onModelChange}
          incognito={incognito}
          isUserNameReady={isUserNameReady}
          userName={userName}
          onActivateAgent={onActivateAgent}
          mentionableAgents={mentionableAgents}
          webSearch={webSearch}
          onWebSearchChange={onWebSearchChange}
          onSend={onSend}
          onStop={onStop}
          status={status}
        />
    </div>
  );
};
